import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from "environments/environment";
import { CrayonProductModel } from 'models/generated/Distribution.Api.Models.Csp.CrayonProduct';
import { CustomerTenantModel } from 'models/generated/Distribution.Api.Models.Csp.CustomerTenant';
import { SubscriptionModel } from 'models/generated/Distribution.Api.Models.Csp.Subscription';
import { SubscriptionDetailsModel } from 'models/generated/Distribution.Api.Models.Csp.SubscriptionDetails';
import { CustomerTenantRequestModel } from 'models/generated/Distribution.Api.Models.CustomerTenantRequest';
import { MicrosoftSubscriptionModel } from 'models/generated/Distribution.Api.Models.MicrosoftSubscription';
import { MicrosoftTenantModel } from 'models/generated/Distribution.Api.Models.MicrosoftTenant';
import { UpdateSubscriptionInitialOrderDataRequestModel } from 'models/generated/Distribution.Api.Models.UpdateSubscriptionInitialOrderDataRequest';
import { firstValueFrom, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable()
export class CspService {
	private baseUrl = `${environment.gatewayUrl}/api/csp`;
	private baseUrlProduct = `${this.baseUrl}/product`;
	private baseUrlTenant = `${this.baseUrl}/tenant`;
	private baseUrlSubscriptions = `${this.baseUrl}/subscription`;

	private _microsoftTenants?: MicrosoftTenantModel[];
	private microsoftTenantsPromise?: Promise<MicrosoftTenantModel[]>;

	constructor(private readonly http: HttpClient) { }

	// Products
	getAllProducts(onlyAnnual: boolean): Promise<CrayonProductModel[]> {
		return firstValueFrom(this.http.get<CrayonProductModel[]>(`${this.baseUrlProduct}?onlyAnnual=${onlyAnnual}`));
	}

	// Tenants
	getMicrosoftTenants(): Promise<MicrosoftTenantModel[]> {
		if (this._microsoftTenants) return new Promise((resolve) => { resolve(this._microsoftTenants!) });
		if (this.microsoftTenantsPromise) return this.microsoftTenantsPromise;
		return this.microsoftTenantsPromise = firstValueFrom(this.http.get<MicrosoftTenantModel[]>(`${this.baseUrlTenant}`)).then(microsoftTenants => {
			this._microsoftTenants = microsoftTenants;
			return microsoftTenants;
		});
	}

	searchTenant(query: string): Observable<CustomerTenantModel[]> {
		return this.http.get<CustomerTenantModel[]>(`${this.baseUrlTenant}/search?keyword=${query}`).pipe(tap());
	}

	getCrayonTenantByTenantId(tenantId: number): Promise<CustomerTenantModel> {
		return firstValueFrom(this.http.get<CustomerTenantModel>(`${this.baseUrlTenant}/crayon/${tenantId}`));
	}

	getMicrosoftTenantByCompanyId(companyId: string): Promise<MicrosoftTenantModel> {
		return firstValueFrom(this.http.get<MicrosoftTenantModel>(`${this.baseUrlTenant}/company/${companyId}`));
	}

	createTenant(request: CustomerTenantRequestModel): Promise<void> {	
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/tenant`, request));
	}

	assignTenantToCustomer(companyId: string, tenantId: number): Promise<void> {
		var request = new MicrosoftTenantModel();
		request.companyId = companyId;
		request.tenantId = tenantId;
		return firstValueFrom(this.http.post<void>(`${this.baseUrlTenant}/assign`, request));
	}

	// Subscriptions
	getCrayonSubscriptions(tenantId: number): Promise<SubscriptionModel[]> {
		return firstValueFrom(this.http.get<SubscriptionModel[]>(`${this.baseUrlSubscriptions}/crayon?tenantId=${tenantId}`));
	}

	getSubscriptionsByProductPlanIds(productPlanIds: string[]): Promise<MicrosoftSubscriptionModel[]> {
		return firstValueFrom(this.http.post<MicrosoftSubscriptionModel[]>(`${this.baseUrlSubscriptions}/product-plans`, productPlanIds));
	}

	getSubscriptionDetails(subscriptionId: number): Promise<SubscriptionDetailsModel>  {
		return firstValueFrom(this.http.get<SubscriptionDetailsModel>(`${this.baseUrlSubscriptions}/details/${subscriptionId}`));
	}

	updateSubscriptionInitialOrderData(request: UpdateSubscriptionInitialOrderDataRequestModel) {
		return firstValueFrom(this.http.put<MicrosoftSubscriptionModel>(`${this.baseUrlSubscriptions}/initialOrderData`, request));
	}

	assignSubscriptionToExistingProductPlan(subscriptionId: number, productPlanId: string, endDate: Date, productPlanTerminated: boolean, crayonAutoRenewEnabled: boolean) {
		return firstValueFrom(this.http.post<MicrosoftSubscriptionModel>(`${this.baseUrlSubscriptions}/assign-subscription-to-existing-product-plan`, { subscriptionId, productPlanId, endDate, productPlanTerminated, crayonAutoRenewEnabled }));
	}
	checkCustomerTenantMicrosoftCustomerAgreementConsented(tenantId: number) {
		return firstValueFrom(this.http.get<boolean>(`${this.baseUrlTenant}/check-consent?tenantId=${tenantId}`));
	}
}
